import React, { useEffect, useState, useContext, useCallback, useMemo } from "react";
import {
  Box, Card, Typography, Button, Grid, Chip, TextField,
  Dialog, DialogTitle, DialogContent, DialogActions,
  Stack, Divider, Tooltip, Tabs, Tab, CircularProgress,
  Collapse, InputAdornment, IconButton, Checkbox,
  Alert, Snackbar,
} from "@mui/material";
import SendIcon               from "@mui/icons-material/Send";
import RefreshIcon            from "@mui/icons-material/Refresh";
import DownloadIcon           from "@mui/icons-material/Download";
import VisibilityIcon         from "@mui/icons-material/Visibility";
import ReceiptLongIcon        from "@mui/icons-material/ReceiptLong";
import AccessTimeIcon         from "@mui/icons-material/AccessTime";
import ChatBubbleOutlineIcon  from "@mui/icons-material/ChatBubbleOutline";
import ExpandMoreIcon         from "@mui/icons-material/ExpandMore";
import ExpandLessIcon         from "@mui/icons-material/ExpandLess";
import BuildIcon              from "@mui/icons-material/Build";
import GavelIcon              from "@mui/icons-material/Gavel";
import ArchitectureIcon       from "@mui/icons-material/Architecture";
import SearchIcon             from "@mui/icons-material/Search";
import CheckCircleIcon        from "@mui/icons-material/CheckCircle";
import EditIcon               from "@mui/icons-material/Edit";
import PictureAsPdfIcon       from "@mui/icons-material/PictureAsPdf";
import HistoryIcon            from "@mui/icons-material/History";
import PendingActionsIcon     from "@mui/icons-material/PendingActions";
import jsPDF                  from "jspdf";
import autoTable              from "jspdf-autotable";
import api                    from "../../../src/utils/api";
import { Link }               from "react-router-dom";
import { PermissionsContext } from "../../../src/utils/PermissionsContext";

// ─── WorkOrderAmounts. shows Labor / Materials / Total on billing cards ──────
function WorkOrderAmounts({ req, fmtMoney }) {
  const laborAmt  = parseFloat(req.labor_price || 0);
  const matAmt    = parseFloat(req.price       || 0);
  const hasLabor  = laborAmt > 0 || !!req.time_billing_code;
  const hasMat    = matAmt   > 0 || !!req.materials_billing_code;
  const showTotal = hasLabor && hasMat;

  if (!hasLabor && !hasMat) {
    return <Typography variant="body2" sx={{ color: "#6b778c", fontStyle: "italic" }}>No amount set</Typography>;
  }
  return (
    <Stack direction="row" spacing={2} alignItems="flex-start" justifyContent="flex-end">
      {hasLabor && (
        <Box>
          <Typography variant="caption" sx={{ color: "#6b778c", display: "block" }}>
            Labor{req.time_billing_code ? ` (${req.time_billing_code})` : ""}
          </Typography>
          <Typography variant="h6" sx={{ fontWeight: 700, color: "#1565c0", lineHeight: 1.1 }}>
            {fmtMoney(laborAmt)}
          </Typography>
        </Box>
      )}
      {hasMat && (
        <Box>
          <Typography variant="caption" sx={{ color: "#6b778c", display: "block" }}>
            Materials{req.materials_billing_code ? ` (${req.materials_billing_code})` : ""}
          </Typography>
          <Typography variant="h6" sx={{ fontWeight: 700, color: "#2e7d32", lineHeight: 1.1 }}>
            {fmtMoney(matAmt)}
          </Typography>
        </Box>
      )}
      {showTotal && (
        <Box sx={{ borderLeft: "2px solid rgba(47,99,173,0.15)", pl: 2 }}>
          <Typography variant="caption" sx={{ color: "#6b778c", display: "block", fontWeight: 600 }}>
            Total
          </Typography>
          <Typography variant="h6" sx={{ fontWeight: 800, color: "#223142", lineHeight: 1.1 }}>
            {fmtMoney(laborAmt + matAmt)}
          </Typography>
        </Box>
      )}
    </Stack>
  );
}

// ─── Style tokens (matches app-wide conventions) ──────────────────────────────

const pageCardSx = {
  borderRadius: "18px",
  border: "1px solid rgba(47,99,173,0.08)",
  boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
  backgroundColor: "rgba(255,255,255,0.96)",
};

const primaryBtnSx = {
  backgroundColor: "#2f63ad",
  color: "white",
  fontWeight: 700,
  borderRadius: "10px",
  textTransform: "none",
  boxShadow: "none",
  "&:hover": { backgroundColor: "#1f4f94", boxShadow: "none" },
};

const secondaryBtnSx = {
  borderRadius: "10px",
  textTransform: "none",
  fontWeight: 600,
  borderColor: "rgba(47,99,173,0.18)",
  color: "#223142",
  "&:hover": { borderColor: "#2f63ad", color: "#2f63ad", backgroundColor: "rgba(47,99,173,0.04)" },
};

const dialogPaperSx = {
  borderRadius: "18px",
  background: "rgba(255,255,255,0.98)",
  boxShadow: "0 20px 60px rgba(0,0,0,0.22)",
};

const inputSx = {
  "& .MuiOutlinedInput-root": {
    backgroundColor: "rgba(255,255,255,0.96)",
    borderRadius: "10px",
  },
};

// ─── Module config ────────────────────────────────────────────────────────────

const MODULE_CONFIG = {
  work_order:    { label: "Work Order",    bg: "#e3ecf7", color: "#1565c0", border: "#2f63ad",   icon: <BuildIcon sx={{ fontSize: 13 }} /> },
  violation:     { label: "Violation",     bg: "#fff3e0", color: "#e65100", border: "#e65100",   icon: <GavelIcon sx={{ fontSize: 13 }} /> },
  architectural: { label: "Architectural", bg: "#e8f5e9", color: "#2e7d32", border: "#2e7d32",   icon: <ArchitectureIcon sx={{ fontSize: 13 }} /> },
  general:       { label: "General",       bg: "#f5f5f5", color: "#616161", border: "#9e9e9e",   icon: <ReceiptLongIcon sx={{ fontSize: 13 }} /> },
};

const getModuleKey = (req) => {
  if (req.work_order_id)            return "work_order";
  if (req.violation_id)             return "violation";
  if (req.architectural_request_id) return "architectural";
  return "general";
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

const fmtDate = (d) => {
  if (!d) return "N/A";
  const dt = new Date(d);
  if (isNaN(dt.getTime()) || dt.getFullYear() < 2000) return "N/A";
  return dt.toLocaleDateString();
};

const fmtDateTime = (d) => {
  if (!d) return "N/A";
  const dt = new Date(d);
  if (isNaN(dt.getTime()) || dt.getFullYear() < 2000) return "N/A";
  return dt.toLocaleString();
};

const fmtMoney = (v) => `$${parseFloat(v || 0).toFixed(2)}`;

const doDownloadCSV = (rows, filename = "jonas-export") => {
  const headers = ["Title", "Ref #", "Member", "Line Item", "Billing Code", "Amount", "Sent At", "Sent By"];
  const body = rows.map(r => [
    `"${(r.title || "").replace(/"/g, '""')}"`,
    r.chit_number || "",
    r.member_number || "",
    `"${(r.export_description || "").replace(/"/g, '""')}"`,
    r.item_group || "",
    parseFloat(r.total || 0).toFixed(2),
    r.sent_at ? new Date(r.sent_at).toLocaleString() : "",
    r.sent_by || "",
  ]);
  const csv  = [headers.join(","), ...body.map(r => r.join(","))].join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement("a");
  a.href = url;
  a.download = `${filename}-${Date.now()}.csv`;
  a.click();
  URL.revokeObjectURL(url);
};

const doDownloadPDF = (rows, title = "Jonas Export Report") => {
  const doc = new jsPDF();
  doc.setFontSize(16);
  doc.setTextColor(34, 49, 66);
  doc.text(title, 14, 18);
  doc.setFontSize(9);
  doc.setTextColor(107, 119, 140);
  doc.text(`Generated: ${new Date().toLocaleString()}`, 14, 25);

  const total = rows.reduce((s, r) => s + parseFloat(r.total || 0), 0);

  autoTable(doc, {
    startY: 30,
    head: [["Title", "Ref #", "Member", "Line Item", "Code", "Amount"]],
    body: rows.map(r => [
      r.title || "N/A",
      r.chit_number || "N/A",
      r.member_number || "N/A",
      r.export_description || "N/A",
      r.item_group || "N/A",
      `$${parseFloat(r.total || 0).toFixed(2)}`,
    ]),
    styles:          { fontSize: 8, cellPadding: 3 },
    headStyles:      { fillColor: [47, 99, 173], fontStyle: "bold" },
    alternateRowStyles: { fillColor: [245, 247, 250] },
    foot:       [["", "", "", "", "Total", `$${total.toFixed(2)}`]],
    footStyles: { fontStyle: "bold", fillColor: [227, 236, 247], textColor: [21, 101, 192] },
  });

  doc.save(`${title.toLowerCase().replace(/\s+/g, "-")}-${Date.now()}.pdf`);
};

// ─── Component ────────────────────────────────────────────────────────────────

const Integrations = () => {
  // ── Data state ──────────────────────────────────────────────────────────────
  const [requests,       setRequests]      = useState([]);
  const [historyRecords, setHistoryRecords]= useState([]);
  const [workOrderTypes, setWorkOrderTypes]= useState([]);

  // ── Tab / filter ────────────────────────────────────────────────────────────
  const [mainTab,        setMainTab]       = useState(0); // 0=pending, 1=history
  const [historyLoading, setHistoryLoading]= useState(false);
  const [moduleFilter,   setModuleFilter]  = useState("all");
  const [searchText,     setSearchText]    = useState("");

  // ── Checkbox selection ──────────────────────────────────────────────────────
  const [selectedIds,    setSelectedIds]   = useState(new Set());

  // ── Card detail expand ──────────────────────────────────────────────────────
  const [expandedCardId, setExpandedCardId]= useState(null);
  const [cardTabMap,     setCardTabMap]    = useState({});
  const [timeLogsCache,  setTimeLogsCache]  = useState({});
  const [commentsCache,  setCommentsCache]  = useState({});
  const [materialsCache, setMaterialsCache] = useState({});
  const [detailLoading,  setDetailLoading]  = useState({});

  // ── Dialogs ─────────────────────────────────────────────────────────────────
  const [editDialog,     setEditDialog]    = useState(null);       // {req, laborPrice, materialsPrice}
  const [confirmDialog,  setConfirmDialog] = useState({ open: false, ids: [] });
  const [reportModal,    setReportModal]   = useState({ open: false, records: [] });

  // ── Misc ────────────────────────────────────────────────────────────────────
  const [sendingIds,     setSendingIds]    = useState(new Set());
  const [snackbar,       setSnackbar]      = useState({ open: false, message: "", severity: "success" });

  const permissions  = useContext(PermissionsContext);
  const hasPermission= (p) => permissions?.includes(p);
  const username     = localStorage.getItem("username");

  const showSnackbar = (message, severity = "success") =>
    setSnackbar({ open: true, message, severity });

  // ── Fetch pending ────────────────────────────────────────────────────────────
  const fetchPending = async () => {
    try {
      const res = await api.get("/billable-requests");
      setRequests(res.data || []);
      setSelectedIds(new Set());
    } catch (err) { console.error(err); }
  };

  // ── Fetch history ────────────────────────────────────────────────────────────
  const fetchHistory = async () => {
    setHistoryLoading(true);
    try {
      const res = await api.get("/jonas-exports");
      setHistoryRecords(res.data || []);
    } catch (err) { console.error(err); }
    finally { setHistoryLoading(false); }
  };

  const fetchWorkOrderTypes = async () => {
    try {
      const res = await api.get("/workorder-types");
      setWorkOrderTypes(res.data || []);
    } catch (err) {}
  };

  useEffect(() => { fetchPending(); fetchWorkOrderTypes(); }, []);

  useEffect(() => {
    if (mainTab === 1 && historyRecords.length === 0) fetchHistory();
  }, [mainTab]); // eslint-disable-line

  // ── Detail panel ─────────────────────────────────────────────────────────────
  const fetchWODetails = useCallback(async (woId) => {
    if (!woId || (timeLogsCache[woId] !== undefined && commentsCache[woId] !== undefined && materialsCache[woId] !== undefined)) return;
    setDetailLoading(p => ({ ...p, [woId]: true }));
    try {
      const [logsRes, cmtRes, matRes] = await Promise.all([
        api.get(`/workorder-time-logs/${woId}`),
        api.get(`/getComments/${woId}`),
        api.get(`/workorder-materials/${woId}`),
      ]);
      setTimeLogsCache(p => ({ ...p, [woId]: logsRes.data || [] }));
      setCommentsCache(p => ({ ...p, [woId]: cmtRes.data  || [] }));
      setMaterialsCache(p => ({ ...p, [woId]: matRes.data || [] }));
    } catch {
      setTimeLogsCache(p => ({ ...p, [woId]: [] }));
      setCommentsCache(p => ({ ...p, [woId]: [] }));
      setMaterialsCache(p => ({ ...p, [woId]: [] }));
    } finally {
      setDetailLoading(p => ({ ...p, [woId]: false }));
    }
  }, [timeLogsCache, commentsCache, materialsCache]);

  const toggleCardDetail = (req) => {
    const isOpen = expandedCardId === req.id;
    setExpandedCardId(isOpen ? null : req.id);
    if (!isOpen && req.work_order_id) {
      if (cardTabMap[req.id] === undefined) setCardTabMap(p => ({ ...p, [req.id]: 0 }));
      fetchWODetails(req.work_order_id);
    }
  };

  // ── Selection ────────────────────────────────────────────────────────────────
  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const selectAll   = () => setSelectedIds(new Set(filteredRequests.map(r => r.id)));
  const clearSelect = () => setSelectedIds(new Set());

  // ── Build export row data ────────────────────────────────────────────────────
  const buildExportRows = (reqList) => {
    const rows = [];
    const now  = new Date().toISOString();
    for (const r of reqList) {
      if (r.work_order_id) {
        if (parseFloat(r.labor_price || 0) > 0) {
          rows.push({
            title: r.title,
            chit_number: String(r.work_order_id).padStart(6, "0"),
            member_number: r.member_number || "N/A",
            export_description: `${r.description || r.title} - LABOR`,
            item_group: r.time_billing_code || "N/A",
            total: parseFloat(r.labor_price).toFixed(2),
            sent_at: now, sent_by: username || "",
          });
        }
        if (parseFloat(r.price || 0) > 0) {
          rows.push({
            title: r.title,
            chit_number: String(r.work_order_id).padStart(6, "0"),
            member_number: r.member_number || "N/A",
            export_description: `${r.description || r.title} - MATERIAL`,
            item_group: r.materials_billing_code || "N/A",
            total: parseFloat(r.price).toFixed(2),
            sent_at: now, sent_by: username || "",
          });
        }
      } else {
        const amt = parseFloat(r.price || 0) + parseFloat(r.labor_price || 0);
        if (amt > 0) {
          rows.push({
            title: r.title,
            chit_number: String(r.violation_id || r.architectural_request_id || r.id).padStart(6, "0"),
            member_number: r.member_number || "N/A",
            export_description: r.description || r.title,
            item_group: r.time_billing_code || r.materials_billing_code || "N/A",
            total: amt.toFixed(2),
            sent_at: now, sent_by: username || "",
          });
        }
      }
    }
    return rows;
  };

  // ── Execute send ─────────────────────────────────────────────────────────────
  const executeSend = async (ids) => {
    const toSend = requests.filter(r => ids.includes(r.id) && r.billable);
    if (!toSend.length) { showSnackbar("No billable records selected.", "warning"); return; }

    const exportRows = buildExportRows(toSend);
    const sentBy     = username || "unknown_user";
    const failed     = [];

    setSendingIds(new Set(ids));
    for (const r of toSend) {
      try { await api.post("/jonas-exports", { billable_request_id: r.id, sent_by: sentBy }); }
      catch (err) { console.error(`Failed ID ${r.id}`, err); failed.push(r.id); }
    }
    setSendingIds(new Set());

    await fetchPending();
    setConfirmDialog({ open: false, ids: [] });

    if (failed.length) {
      showSnackbar(`${toSend.length - failed.length} sent, ${failed.length} failed.`, "warning");
    } else {
      showSnackbar(`${toSend.length} record(s) sent to Jonas successfully.`, "success");
    }
    if (exportRows.length) setReportModal({ open: true, records: exportRows });
  };

  const handleSendOne      = (req) => setConfirmDialog({ open: true, ids: [req.id] });
  const handleSendSelected = () => {
    const ids = Array.from(selectedIds);
    if (!ids.length) { showSnackbar("No records selected.", "warning"); return; }
    setConfirmDialog({ open: true, ids });
  };
  const handleSendAll = () => {
    const ids = filteredRequests.filter(r => r.billable).map(r => r.id);
    if (!ids.length) { showSnackbar("No billable records to send.", "warning"); return; }
    setConfirmDialog({ open: true, ids });
  };

  // ── Edit dialog ──────────────────────────────────────────────────────────────
  const openEditDialog = (req) => setEditDialog({
    req,
    laborPrice:     String(parseFloat(req.labor_price || 0).toFixed(2)),
    materialsPrice: String(parseFloat(req.price       || 0).toFixed(2)),
  });

  const saveEdit = async () => {
    if (!editDialog) return;
    const { req, laborPrice, materialsPrice } = editDialog;
    try {
      await api.put(`/billable-requests/${req.id}`, {
        ...req,
        price:       parseFloat(materialsPrice) || 0,
        labor_price: parseFloat(laborPrice)     || 0,
      });
      setRequests(prev => prev.map(r =>
        r.id === req.id
          ? { ...r, price: parseFloat(materialsPrice) || 0, labor_price: parseFloat(laborPrice) || 0 }
          : r
      ));
      setEditDialog(null);
      showSnackbar("Pricing updated successfully.");
    } catch (err) {
      console.error(err);
      showSnackbar("Failed to save changes.", "error");
    }
  };

  // ── Computed ─────────────────────────────────────────────────────────────────
  const getTypeName = (req) => {
    if (req.work_order_id && req.work_order_type) {
      const m = workOrderTypes.find(w => String(w.id) === String(req.work_order_type));
      return m ? m.type : "N/A";
    }
    return req.violation_type_name || req.architectural_type_name || "N/A";
  };

  const stats = useMemo(() => {
    const out = { total: requests.length, totalAmount: 0, byModule: {} };
    for (const r of requests) {
      const amt = parseFloat(r.price || 0) + parseFloat(r.labor_price || 0);
      out.totalAmount += amt;
      const mk = getModuleKey(r);
      if (!out.byModule[mk]) out.byModule[mk] = { count: 0, amount: 0 };
      out.byModule[mk].count++;
      out.byModule[mk].amount += amt;
    }
    return out;
  }, [requests]);

  const filteredRequests = useMemo(() => requests.filter(r => {
    if (moduleFilter !== "all" && getModuleKey(r) !== moduleFilter) return false;
    if (searchText) {
      const q = searchText.toLowerCase();
      return (
        r.title?.toLowerCase().includes(q) ||
        r.member_number?.toLowerCase().includes(q) ||
        String(r.work_order_id || "").includes(q)
      );
    }
    return true;
  }), [requests, moduleFilter, searchText]);

  // Group history by billable_request_id for display
  const groupedHistory = useMemo(() => {
    const groups = {};
    for (const row of historyRecords) {
      const key = String(row.billable_request_id ?? `s_${row.id}`);
      if (!groups[key]) {
        groups[key] = {
          key, title: row.title, work_order_id: row.work_order_id,
          violation_id: row.violation_id, chit_number: row.chit_number,
          member_number: row.member_number, sent_at: row.sent_at,
          sent_by: row.sent_by, rows: [], total_amount: 0,
        };
      }
      groups[key].rows.push(row);
      groups[key].total_amount += parseFloat(row.total || 0);
    }
    return Object.values(groups).sort((a, b) => new Date(b.sent_at) - new Date(a.sent_at));
  }, [historyRecords]);

  const confirmItems = useMemo(() =>
    requests.filter(r => confirmDialog.ids.includes(r.id) && r.billable),
  [requests, confirmDialog.ids]);

  const confirmTotal = confirmItems.reduce(
    (s, r) => s + parseFloat(r.price || 0) + parseFloat(r.labor_price || 0), 0
  );

  // ─── Render ───────────────────────────────────────────────────────────────

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1100, mx: "auto" }}>

      {/* Page Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 700, color: "#223142", mb: 0.5 }}>
          Billable Requests
        </Typography>
        <Typography variant="body2" sx={{ color: "#6b778c" }}>
          Review, adjust pricing, and export billing records to Jonas.
        </Typography>
      </Box>

      {/* Stats Row */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {[
          { label: "PENDING REVIEW",  value: stats.total,                                 sub: `of ${stats.total} total`,                               color: "#2f63ad" },
          { label: "TOTAL AMOUNT",    value: fmtMoney(stats.totalAmount),                 sub: "labor + materials",                                     color: "#2e7d32" },
          { label: "WORK ORDERS",     value: stats.byModule.work_order?.count   || 0,     sub: fmtMoney(stats.byModule.work_order?.amount   || 0),      color: "#1565c0" },
          { label: "VIOLATIONS",      value: stats.byModule.violation?.count    || 0,     sub: fmtMoney(stats.byModule.violation?.amount    || 0),      color: "#e65100" },
        ].map((s, i) => (
          <Grid item xs={12} sm={6} md={3} key={i}>
            <Card sx={{ ...pageCardSx, p: 2.5 }}>
              <Typography variant="caption" sx={{ color: "#6b778c", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.8 }}>
                {s.label}
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 800, color: s.color, my: 0.5 }}>
                {s.value}
              </Typography>
              <Typography variant="caption" sx={{ color: "#6b778c" }}>{s.sub}</Typography>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Main Card with Tabs */}
      <Card sx={{ ...pageCardSx, overflow: "hidden" }}>

        {/* Tab bar */}
        <Box sx={{ borderBottom: "1px solid rgba(47,99,173,0.08)" }}>
          <Tabs
            value={mainTab}
            onChange={(_, v) => setMainTab(v)}
            sx={{
              px: 2,
              "& .MuiTab-root":   { fontWeight: 600, textTransform: "none", color: "#6b778c", minHeight: 52 },
              "& .Mui-selected":  { color: "#2f63ad !important" },
              "& .MuiTabs-indicator": { backgroundColor: "#2f63ad" },
            }}
          >
            <Tab label={
              <Stack direction="row" alignItems="center" spacing={0.75}>
                <PendingActionsIcon sx={{ fontSize: 17 }} />
                <span>Pending Review</span>
                {requests.length > 0 && (
                  <Chip label={requests.length} size="small"
                    sx={{ backgroundColor: "#2f63ad", color: "white", fontWeight: 700, height: 19, fontSize: 11, "& .MuiChip-label": { px: 0.75 } }} />
                )}
              </Stack>
            } />
            <Tab label={
              <Stack direction="row" alignItems="center" spacing={0.75}>
                <HistoryIcon sx={{ fontSize: 17 }} />
                <span>Sent History</span>
              </Stack>
            } />
          </Tabs>
        </Box>

        {/* ── Pending Review ─────────────────────────────────────────────── */}
        {mainTab === 0 && (
          <Box sx={{ p: { xs: 1.5, md: 2.5 } }}>

            {/* Filters + search */}
            <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} alignItems={{ md: "center" }} sx={{ mb: 2 }}>
              <Stack direction="row" spacing={0.75} flexWrap="wrap" sx={{ flex: 1 }}>
                {[
                  { key: "all",           label: `All (${requests.length})` },
                  { key: "work_order",    label: `Work Orders (${stats.byModule.work_order?.count    || 0})` },
                  { key: "violation",     label: `Violations (${stats.byModule.violation?.count     || 0})` },
                  { key: "architectural", label: `Architectural (${stats.byModule.architectural?.count || 0})` },
                ].map(f => (
                  <Chip key={f.key} label={f.label} onClick={() => setModuleFilter(f.key)}
                    variant={moduleFilter === f.key ? "filled" : "outlined"}
                    sx={{
                      fontWeight: 600, cursor: "pointer",
                      ...(moduleFilter === f.key
                        ? { backgroundColor: "#2f63ad", color: "white" }
                        : { borderColor: "rgba(47,99,173,0.18)", color: "#223142" }),
                    }}
                  />
                ))}
              </Stack>
              <Stack direction="row" spacing={1}>
                <TextField size="small" placeholder="Search…" value={searchText}
                  onChange={e => setSearchText(e.target.value)}
                  InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 17, color: "#6b778c" }} /></InputAdornment> }}
                  sx={{ ...inputSx, width: 190 }}
                />
                <Tooltip title="Reload">
                  <IconButton onClick={fetchPending} sx={{ border: "1px solid rgba(47,99,173,0.14)", borderRadius: "10px" }}>
                    <RefreshIcon sx={{ color: "#2f63ad" }} />
                  </IconButton>
                </Tooltip>
              </Stack>
            </Stack>

            {/* Selection / bulk-action bar */}
            {filteredRequests.length > 0 && (
              <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap"
                sx={{ mb: 2, px: 1.5, py: 1, borderRadius: "12px", border: "1px solid rgba(47,99,173,0.10)", backgroundColor: "rgba(47,99,173,0.03)" }}>
                <Tooltip title={selectedIds.size === filteredRequests.length ? "Deselect all" : "Select all"}>
                  <Checkbox
                    checked={selectedIds.size === filteredRequests.length && filteredRequests.length > 0}
                    indeterminate={selectedIds.size > 0 && selectedIds.size < filteredRequests.length}
                    onChange={() => selectedIds.size === filteredRequests.length ? clearSelect() : selectAll()}
                    sx={{ p: 0.5, "& .MuiSvgIcon-root": { color: "#2f63ad" } }}
                  />
                </Tooltip>
                <Typography variant="body2" sx={{ color: "#6b778c", flex: 1 }}>
                  {selectedIds.size > 0
                    ? `${selectedIds.size} of ${filteredRequests.length} selected`
                    : `${filteredRequests.length} record(s) pending`}
                </Typography>
                {selectedIds.size > 0 && (
                  <Button variant="contained" size="small" startIcon={<SendIcon sx={{ fontSize: 15 }} />}
                    onClick={handleSendSelected} sx={{ ...primaryBtnSx, py: 0.75, px: 2 }}>
                    Send Selected ({selectedIds.size})
                  </Button>
                )}
                <Button variant="contained" size="small" startIcon={<SendIcon sx={{ fontSize: 15 }} />}
                  onClick={handleSendAll} sx={{ ...primaryBtnSx, py: 0.75, px: 2 }}>
                  Send All to Jonas
                </Button>
              </Stack>
            )}

            {/* Cards */}
            {filteredRequests.length === 0 ? (
              <Box sx={{ textAlign: "center", py: 8 }}>
                <CheckCircleIcon sx={{ fontSize: 52, color: "#2f63ad", opacity: 0.25, mb: 1.5 }} />
                <Typography sx={{ fontWeight: 700, color: "#223142", mb: 0.5 }}>All caught up!</Typography>
                <Typography variant="body2" sx={{ color: "#6b778c" }}>No pending billing records.</Typography>
              </Box>
            ) : (
              <Stack spacing={2}>
                {filteredRequests.map(req => {
                  const mk        = getModuleKey(req);
                  const cfg       = MODULE_CONFIG[mk];
                  const isSending = sendingIds.has(req.id);
                  const isExpanded= expandedCardId === req.id;
                  const woId      = req.work_order_id;
                  const timeLogs  = timeLogsCache[woId]  || [];
                  const comments  = commentsCache[woId]  || [];
                  const materials = materialsCache[woId] || [];
                  const cardTab   = cardTabMap[req.id] ?? 0;

                  return (
                    <Card key={req.id} sx={{
                      borderRadius: "16px",
                      border: "1px solid rgba(47,99,173,0.08)",
                      borderLeft: `4px solid ${cfg.border}`,
                      boxShadow: "none",
                      overflow: "hidden",
                      transition: "box-shadow 0.2s ease",
                      "&:hover": { boxShadow: "0 4px 20px rgba(47,99,173,0.10)" },
                    }}>
                      <Box sx={{ p: { xs: 1.5, md: 2 } }}>
                        <Stack direction="row" alignItems="flex-start" spacing={1.25}>

                          {/* Checkbox */}
                          <Checkbox checked={selectedIds.has(req.id)} onChange={() => toggleSelect(req.id)}
                            sx={{ p: 0.5, mt: 0.25, "& .MuiSvgIcon-root": { color: "#2f63ad" } }} />

                          {/* Content */}
                          <Box sx={{ flex: 1, minWidth: 0 }}>

                            {/* Top row: module chip + title + amounts */}
                            <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between"
                              alignItems={{ sm: "flex-start" }} spacing={1} sx={{ mb: 1.5 }}>
                              <Box sx={{ minWidth: 0 }}>
                                <Stack direction="row" spacing={0.75} flexWrap="wrap" alignItems="center" sx={{ mb: 0.75 }}>
                                  <Chip icon={cfg.icon} label={cfg.label} size="small"
                                    sx={{ backgroundColor: cfg.bg, color: cfg.color, fontWeight: 600 }} />
                                  {req.billable && <Chip label="Billable" size="small" color="success" />}
                                </Stack>
                                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "#223142" }}>
                                  {req.title || `Record #${req.id}`}
                                </Typography>
                                {req.description && (
                                  <Typography variant="body2" sx={{ color: "#6b778c", mt: 0.25 }}>
                                    {req.description}
                                  </Typography>
                                )}
                              </Box>

                              {/* Amount display */}
                              <Box sx={{ flexShrink: 0, textAlign: { sm: "right" } }}>
                                {mk === "work_order" ? (
                                  <WorkOrderAmounts req={req} fmtMoney={fmtMoney} />
                                ) : (
                                  <Box>
                                    <Typography variant="caption" sx={{ color: "#6b778c", display: "block" }}>
                                      {mk === "violation" ? "Fine" : "Fee"}
                                    </Typography>
                                    <Typography variant="h6" sx={{ fontWeight: 700, color: "#e65100", lineHeight: 1.1 }}>
                                      {fmtMoney(req.price)}
                                    </Typography>
                                  </Box>
                                )}
                              </Box>
                            </Stack>

                            {/* Info chips */}
                            <Stack direction="row" spacing={2} flexWrap="wrap" sx={{ mb: 1.5 }}>
                              {[
                                { label: "TYPE",   value: getTypeName(req) },
                                req.work_order_id  ? { label: "WO",      value: `#${req.work_order_id}` }  : null,
                                req.violation_id   ? { label: "VIO",     value: `#${req.violation_id}` }   : null,
                                { label: "CLOSED", value: fmtDate(req.closed_at) },
                                req.member_number  ? { label: "MEMBER",  value: req.member_number }        : null,
                              ].filter(Boolean).map((inf, i) => (
                                <Box key={i}>
                                  <Typography variant="caption" sx={{ color: "#6b778c", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.6, display: "block" }}>
                                    {inf.label}
                                  </Typography>
                                  <Typography variant="body2" sx={{ color: "#223142", fontWeight: 600 }}>
                                    {inf.value}
                                  </Typography>
                                </Box>
                              ))}
                            </Stack>

                            {/* Action buttons */}
                            <Stack direction="row" spacing={1} flexWrap="wrap" alignItems="center">
                              <Button variant="contained" size="small"
                                startIcon={isSending ? <CircularProgress size={14} color="inherit" /> : <SendIcon sx={{ fontSize: 15 }} />}
                                onClick={() => handleSendOne(req)}
                                disabled={isSending || !req.billable}
                                sx={{ ...primaryBtnSx, py: 0.75, px: 2 }}>
                                {isSending ? "Sending…" : "Send to Jonas"}
                              </Button>

                              <Button variant="outlined" size="small" startIcon={<EditIcon sx={{ fontSize: 15 }} />}
                                onClick={() => openEditDialog(req)} sx={{ ...secondaryBtnSx, py: 0.75, px: 1.75 }}>
                                Edit
                              </Button>

                              {woId && (
                                <Button component={Link} to={`/home/workorders/${woId}`}
                                  variant="outlined" size="small" startIcon={<VisibilityIcon sx={{ fontSize: 15 }} />}
                                  sx={{ ...secondaryBtnSx, py: 0.75, px: 1.75 }}>
                                  View WO
                                </Button>
                              )}

                              {woId && (
                                <Button variant="text" size="small"
                                  endIcon={isExpanded ? <ExpandLessIcon sx={{ fontSize: 16 }} /> : <ExpandMoreIcon sx={{ fontSize: 16 }} />}
                                  onClick={() => toggleCardDetail(req)}
                                  sx={{ color: "#6b778c", textTransform: "none", fontWeight: 600, fontSize: 13 }}>
                                  {isExpanded ? "Hide" : "Show"} details
                                  {timeLogs.length > 0 && ` (${timeLogs.length})`}
                                </Button>
                              )}
                            </Stack>

                            {/* Expandable detail panel */}
                            {woId && (
                              <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                                <Divider sx={{ my: 2, borderColor: "rgba(47,99,173,0.08)" }} />
                                <Tabs value={cardTab}
                                  onChange={(_, v) => setCardTabMap(p => ({ ...p, [req.id]: v }))}
                                  sx={{
                                    mb: 1.5, minHeight: 36,
                                    "& .MuiTab-root":  { textTransform: "none", fontWeight: 600, fontSize: 13, minHeight: 36 },
                                    "& .Mui-selected": { color: "#2f63ad !important" },
                                    "& .MuiTabs-indicator": { backgroundColor: "#2f63ad" },
                                  }}>
                                  <Tab label={
                                    <Stack direction="row" spacing={0.5} alignItems="center">
                                      <AccessTimeIcon sx={{ fontSize: 14 }} />
                                      <span>Time Logs</span>
                                      {timeLogs.length > 0 && (
                                        <Chip label={timeLogs.length} size="small"
                                          sx={{ height: 17, fontSize: 10, "& .MuiChip-label": { px: 0.6 } }} />
                                      )}
                                    </Stack>
                                  } />
                                  <Tab label={
                                    <Stack direction="row" spacing={0.5} alignItems="center">
                                      <BuildIcon sx={{ fontSize: 14 }} />
                                      <span>Materials</span>
                                      {materials.length > 0 && (
                                        <Chip label={materials.length} size="small"
                                          sx={{ height: 17, fontSize: 10, "& .MuiChip-label": { px: 0.6 } }} />
                                      )}
                                    </Stack>
                                  } />
                                  <Tab label={
                                    <Stack direction="row" spacing={0.5} alignItems="center">
                                      <ChatBubbleOutlineIcon sx={{ fontSize: 14 }} />
                                      <span>Comments</span>
                                      {comments.length > 0 && (
                                        <Chip label={comments.length} size="small"
                                          sx={{ height: 17, fontSize: 10, "& .MuiChip-label": { px: 0.6 } }} />
                                      )}
                                    </Stack>
                                  } />
                                </Tabs>

                                {detailLoading[woId] ? (
                                  <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
                                    <CircularProgress size={24} />
                                  </Box>
                                ) : cardTab === 0 ? (
                                  /* ── Time Logs tab ── */
                                  timeLogs.length === 0
                                    ? <Typography variant="body2" sx={{ color: "#6b778c", py: 2 }}>No time logs recorded.</Typography>
                                    : timeLogs.map((log, li) => {
                                        const billableHrs = timeLogs.filter(l => l.is_billable).reduce((s, l) => s + Number(l.hours || 0), 0);
                                        const ruleRate    = billableHrs > 0 && parseFloat(req.labor_price || 0) > 0
                                          ? parseFloat(req.labor_price) / billableHrs : null;
                                        return (
                                          <Box key={li} sx={{ p: 1.5, mb: 1, borderRadius: "10px", border: "1px solid rgba(47,99,173,0.08)", backgroundColor: "rgba(47,99,173,0.025)" }}>
                                            <Stack direction="row" spacing={0.75} flexWrap="wrap" sx={{ mb: 0.5 }}>
                                              <Chip label={`${Number(log.hours || 0).toFixed(2)} hrs`} size="small" sx={{ fontWeight: 700 }} />
                                              <Chip label={log.is_billable ? "Billable" : "Non-billable"} size="small" color={log.is_billable ? "success" : "default"} />
                                              {ruleRate !== null && log.is_billable && (
                                                <Chip label={`Billed @ $${ruleRate.toFixed(2)}/hr (billing rule)`} size="small"
                                                  sx={{ backgroundColor: "#e3f2fd", color: "#1565c0", fontWeight: 600 }} />
                                              )}
                                            </Stack>
                                            {log.description && (
                                              <Typography variant="body2" sx={{ color: "#223142" }}>{log.description}</Typography>
                                            )}
                                          </Box>
                                        );
                                      })
                                ) : cardTab === 1 ? (
                                  /* ── Materials tab ── */
                                  materials.length === 0
                                    ? <Typography variant="body2" sx={{ color: "#6b778c", py: 2 }}>No materials recorded.</Typography>
                                    : <>
                                        {materials.map((mat, mi) => (
                                          <Box key={mi} sx={{ p: 1.5, mb: 1, borderRadius: "10px", border: "1px solid rgba(46,125,50,0.12)", backgroundColor: "rgba(46,125,50,0.03)" }}>
                                            <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                                              <Box sx={{ flex: 1, minWidth: 0 }}>
                                                <Typography variant="body2" sx={{ fontWeight: 600, color: "#223142" }}>
                                                  {mat.description || "N/A"}
                                                </Typography>
                                                <Stack direction="row" spacing={0.75} flexWrap="wrap" sx={{ mt: 0.5 }}>
                                                  <Chip label={`Qty: ${mat.quantity ?? 1}`} size="small" sx={{ fontWeight: 600 }} />
                                                  <Chip label={`$${parseFloat(mat.unit_price || 0).toFixed(2)} each`} size="small" sx={{ color: "#6b778c" }} />
                                                  <Chip
                                                    label={mat.is_billable ? "Billable" : "Non-billable"}
                                                    size="small"
                                                    color={mat.is_billable ? "success" : "default"}
                                                  />
                                                  {mat.item_group_code && (
                                                    <Chip label={`Code: ${mat.item_group_code}`} size="small" sx={{ color: "#6b778c" }} />
                                                  )}
                                                </Stack>
                                                {mat.notes && (
                                                  <Typography variant="caption" sx={{ color: "#6b778c", display: "block", mt: 0.5 }}>
                                                    {mat.notes}
                                                  </Typography>
                                                )}
                                              </Box>
                                              <Typography variant="body1" sx={{ fontWeight: 700, color: "#2e7d32", ml: 2, flexShrink: 0 }}>
                                                {fmtMoney((mat.quantity ?? 1) * parseFloat(mat.unit_price || 0))}
                                              </Typography>
                                            </Stack>
                                          </Box>
                                        ))}
                                        <Box sx={{ pt: 1, borderTop: "1px solid rgba(46,125,50,0.12)", display: "flex", justifyContent: "flex-end" }}>
                                          <Typography variant="body2" sx={{ fontWeight: 700, color: "#2e7d32" }}>
                                            Total (billable): {fmtMoney(
                                              materials.filter(m => m.is_billable).reduce((s, m) => s + (m.quantity ?? 1) * parseFloat(m.unit_price || 0), 0)
                                            )}
                                          </Typography>
                                        </Box>
                                      </>
                                ) : (
                                  /* ── Comments tab ── */
                                  comments.length === 0
                                    ? <Typography variant="body2" sx={{ color: "#6b778c", py: 2 }}>No comments.</Typography>
                                    : comments.map((c, ci) => (
                                        <Box key={ci} sx={{ p: 1.5, mb: 1, borderRadius: "10px", border: "1px solid rgba(47,99,173,0.08)", backgroundColor: "rgba(47,99,173,0.025)" }}>
                                          <Typography variant="caption" sx={{ color: "#6b778c", display: "block", mb: 0.5 }}>
                                            {c.username || "User"} · {fmtDate(c.created_at)}
                                          </Typography>
                                          <Typography variant="body2" sx={{ color: "#223142" }}>
                                            {c.comment || c.text || c.body || "N/A"}
                                          </Typography>
                                        </Box>
                                      ))
                                )}
                              </Collapse>
                            )}
                          </Box>
                        </Stack>
                      </Box>
                    </Card>
                  );
                })}
              </Stack>
            )}
          </Box>
        )}

        {/* ── Sent History ─────────────────────────────────────────────────── */}
        {mainTab === 1 && (
          <Box sx={{ p: { xs: 1.5, md: 2.5 } }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1} sx={{ mb: 2.5 }}>
              <Typography variant="body2" sx={{ color: "#6b778c" }}>
                {groupedHistory.length} export group(s) sent to Jonas
              </Typography>
              <Stack direction="row" spacing={1}>
                <Button variant="outlined" size="small" startIcon={<RefreshIcon sx={{ fontSize: 15 }} />}
                  onClick={fetchHistory} disabled={historyLoading}
                  sx={{ ...secondaryBtnSx, py: 0.75, px: 1.75 }}>
                  Reload
                </Button>
                {groupedHistory.length > 0 && (
                  <>
                    <Button variant="outlined" size="small" startIcon={<DownloadIcon sx={{ fontSize: 15 }} />}
                      onClick={() => doDownloadCSV(historyRecords, "jonas-history")}
                      sx={{ ...secondaryBtnSx, py: 0.75, px: 1.75 }}>
                      Export CSV
                    </Button>
                    <Button variant="outlined" size="small" startIcon={<PictureAsPdfIcon sx={{ fontSize: 15 }} />}
                      onClick={() => doDownloadPDF(historyRecords, "Jonas Export History")}
                      sx={{ ...secondaryBtnSx, py: 0.75, px: 1.75 }}>
                      Export PDF
                    </Button>
                  </>
                )}
              </Stack>
            </Stack>

            {historyLoading ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                <CircularProgress />
              </Box>
            ) : groupedHistory.length === 0 ? (
              <Box sx={{ textAlign: "center", py: 8 }}>
                <HistoryIcon sx={{ fontSize: 52, color: "#2f63ad", opacity: 0.25, mb: 1.5 }} />
                <Typography sx={{ fontWeight: 700, color: "#223142", mb: 0.5 }}>No exports yet</Typography>
                <Typography variant="body2" sx={{ color: "#6b778c" }}>Records sent to Jonas will appear here.</Typography>
              </Box>
            ) : (
              <Stack spacing={1.5}>
                {groupedHistory.map((group) => (
                  <Card key={group.key} sx={{
                    borderRadius: "14px",
                    border: "1px solid rgba(47,99,173,0.08)",
                    boxShadow: "none",
                    overflow: "hidden",
                  }}>
                    {/* Group header */}
                    <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1}
                      sx={{ px: 2.5, py: 1.75, backgroundColor: "rgba(47,99,173,0.03)", borderBottom: "1px solid rgba(47,99,173,0.06)" }}>
                      <Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#223142" }}>
                          {group.title || `Record #${group.billable_request_id}`}
                        </Typography>
                        <Typography variant="caption" sx={{ color: "#6b778c" }}>
                          Sent {fmtDateTime(group.sent_at)} by <strong>{group.sent_by || "N/A"}</strong>
                          {group.chit_number  && ` · Chit #${group.chit_number}`}
                          {group.member_number && ` · Member ${group.member_number}`}
                        </Typography>
                      </Box>
                      <Typography variant="h6" sx={{ fontWeight: 700, color: "#2e7d32" }}>
                        {fmtMoney(group.total_amount)}
                      </Typography>
                    </Stack>

                    {/* Line items */}
                    <Box sx={{ px: 2.5, py: 0.5 }}>
                      {group.rows.map((row, ri) => (
                        <Stack key={ri} direction="row" justifyContent="space-between" alignItems="center"
                          sx={{ py: 1, borderBottom: ri < group.rows.length - 1 ? "1px solid rgba(47,99,173,0.06)" : "none" }}>
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 600, color: "#223142" }}>
                              {row.export_description || row.title || "N/A"}
                            </Typography>
                            {row.item_group && (
                              <Chip label={row.item_group} size="small"
                                sx={{ mt: 0.25, height: 18, fontSize: 10, backgroundColor: "rgba(47,99,173,0.08)", color: "#2f63ad", "& .MuiChip-label": { px: 0.75 } }} />
                            )}
                          </Box>
                          <Typography variant="body2" sx={{ fontWeight: 700, color: "#223142" }}>
                            {fmtMoney(row.total)}
                          </Typography>
                        </Stack>
                      ))}
                    </Box>
                  </Card>
                ))}
              </Stack>
            )}
          </Box>
        )}
      </Card>

      {/* ── Edit Dialog ───────────────────────────────────────────────────── */}
      <Dialog open={!!editDialog} onClose={() => setEditDialog(null)} maxWidth="xs" fullWidth PaperProps={{ sx: dialogPaperSx }}>
        <DialogTitle sx={{ pb: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, color: "#223142" }}>Edit Pricing</Typography>
          {editDialog && (
            <Typography variant="body2" sx={{ color: "#6b778c", fontWeight: 400, mt: 0.25 }}>
              {editDialog.req.title}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2.5} sx={{ pt: 1 }}>
            {editDialog && (() => {
              const req = editDialog.req;
              const isWO = !!req.work_order_id;
              const showLabor = isWO && (req.time_billing_code || parseFloat(req.labor_price || 0) > 0);
              const showMats  = isWO && (req.materials_billing_code || parseFloat(req.price || 0) > 0);
              return (
                <>
                  {isWO && showLabor && (
                    <TextField
                      label={`Labor Price${req.time_billing_code ? ` (${req.time_billing_code})` : ""}`}
                      type="number" fullWidth
                      value={editDialog.laborPrice}
                      onChange={e => setEditDialog(p => ({ ...p, laborPrice: e.target.value }))}
                      inputProps={{ min: 0, step: 0.01 }}
                      sx={inputSx}
                      InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                    />
                  )}
                  {isWO && showMats && (
                    <TextField
                      label={`Materials Price${req.materials_billing_code ? ` (${req.materials_billing_code})` : ""}`}
                      type="number" fullWidth
                      value={editDialog.materialsPrice}
                      onChange={e => setEditDialog(p => ({ ...p, materialsPrice: e.target.value }))}
                      inputProps={{ min: 0, step: 0.01 }}
                      sx={inputSx}
                      InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                    />
                  )}
                  {!isWO && (
                    <TextField
                      label={getModuleKey(req) === "violation" ? "Fine Amount" : "Fee Amount"}
                      type="number" fullWidth
                      value={editDialog.materialsPrice}
                      onChange={e => setEditDialog(p => ({ ...p, materialsPrice: e.target.value }))}
                      inputProps={{ min: 0, step: 0.01 }}
                      sx={inputSx}
                      InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                    />
                  )}
                </>
              );
            })()}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, pt: 2, gap: 1 }}>
          <Button variant="outlined" onClick={() => setEditDialog(null)} sx={secondaryBtnSx}>Cancel</Button>
          <Button variant="contained" onClick={saveEdit} sx={primaryBtnSx}>Save Changes</Button>
        </DialogActions>
      </Dialog>

      {/* ── Confirm Send Dialog ────────────────────────────────────────────── */}
      <Dialog open={confirmDialog.open} onClose={() => setConfirmDialog({ open: false, ids: [] })}
        maxWidth="sm" fullWidth PaperProps={{ sx: dialogPaperSx }}>
        <DialogTitle sx={{ fontWeight: 700, color: "#223142" }}>
          Confirm. Send to Jonas
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" sx={{ color: "#6b778c", mb: 2 }}>
            {confirmItems.length} record(s) will be sent:
          </Typography>
          <Stack spacing={0.75}>
            {confirmItems.map(r => (
              <Stack key={r.id} direction="row" justifyContent="space-between" alignItems="center"
                sx={{ px: 1.5, py: 1.25, borderRadius: "10px", border: "1px solid rgba(47,99,173,0.08)", backgroundColor: "rgba(47,99,173,0.02)" }}>
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 700, color: "#223142" }}>{r.title}</Typography>
                  <Typography variant="caption" sx={{ color: "#6b778c" }}>{getTypeName(r)}</Typography>
                </Box>
                <Typography variant="body2" sx={{ fontWeight: 700, color: "#1565c0" }}>
                  {fmtMoney(parseFloat(r.price || 0) + parseFloat(r.labor_price || 0))}
                </Typography>
              </Stack>
            ))}
          </Stack>
          <Divider sx={{ my: 2, borderColor: "rgba(47,99,173,0.08)" }} />
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="body2" sx={{ fontWeight: 600, color: "#6b778c" }}>Total</Typography>
            <Typography variant="h6" sx={{ fontWeight: 800, color: "#2f63ad" }}>{fmtMoney(confirmTotal)}</Typography>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, pt: 2, gap: 1 }}>
          <Button variant="outlined" onClick={() => setConfirmDialog({ open: false, ids: [] })} sx={secondaryBtnSx}>Cancel</Button>
          <Button variant="contained" startIcon={<SendIcon sx={{ fontSize: 16 }} />}
            onClick={() => executeSend(confirmDialog.ids)} sx={primaryBtnSx}>
            Confirm &amp; Send
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Post-Send Report Modal ─────────────────────────────────────────── */}
      <Dialog open={reportModal.open} onClose={() => setReportModal({ open: false, records: [] })}
        maxWidth="sm" fullWidth PaperProps={{ sx: dialogPaperSx }}>
        <DialogTitle>
          <Stack direction="row" spacing={1} alignItems="center">
            <CheckCircleIcon sx={{ color: "#2e7d32", fontSize: 24 }} />
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, color: "#223142" }}>Sent Successfully</Typography>
              <Typography variant="caption" sx={{ color: "#6b778c" }}>
                {reportModal.records.length} line item(s) exported · Total {fmtMoney(reportModal.records.reduce((s, r) => s + parseFloat(r.total || 0), 0))}
              </Typography>
            </Box>
          </Stack>
        </DialogTitle>
        <DialogContent dividers>
          {/* Summary table */}
          <Box sx={{ borderRadius: "10px", overflow: "hidden", border: "1px solid rgba(47,99,173,0.08)", mb: 2.5 }}>
            {reportModal.records.map((r, i) => (
              <Stack key={i} direction="row" justifyContent="space-between" alignItems="center"
                sx={{ px: 2, py: 1.25, backgroundColor: i % 2 === 0 ? "rgba(47,99,173,0.02)" : "white",
                  borderBottom: i < reportModal.records.length - 1 ? "1px solid rgba(47,99,173,0.06)" : "none" }}>
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: "#223142" }}>
                    {r.export_description || r.title}
                  </Typography>
                  {r.item_group && r.item_group !== "N/A" && (
                    <Typography variant="caption" sx={{ color: "#6b778c" }}>{r.item_group}</Typography>
                  )}
                </Box>
                <Typography variant="body2" sx={{ fontWeight: 700, color: "#2f63ad" }}>
                  {fmtMoney(r.total)}
                </Typography>
              </Stack>
            ))}
          </Box>

          {/* Download buttons */}
          <Typography variant="body2" sx={{ fontWeight: 600, color: "#223142", mb: 1.5 }}>
            Download report:
          </Typography>
          <Stack direction="row" spacing={1.5}>
            <Button fullWidth variant="outlined" startIcon={<DownloadIcon />}
              onClick={() => doDownloadCSV(reportModal.records, "jonas-export")}
              sx={secondaryBtnSx}>
              Download CSV
            </Button>
            <Button fullWidth variant="contained" startIcon={<PictureAsPdfIcon />}
              onClick={() => doDownloadPDF(reportModal.records, "Jonas Export Report")}
              sx={primaryBtnSx}>
              Download PDF
            </Button>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, pt: 2 }}>
          <Button variant="outlined" onClick={() => setReportModal({ open: false, records: [] })} sx={secondaryBtnSx}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* ── Snackbar ──────────────────────────────────────────────────────── */}
      <Snackbar open={snackbar.open} autoHideDuration={4000}
        onClose={() => setSnackbar(p => ({ ...p, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
        <Alert onClose={() => setSnackbar(p => ({ ...p, open: false }))}
          severity={snackbar.severity} variant="filled"
          sx={{ borderRadius: 3, width: "100%" }}>
          {snackbar.message}
        </Alert>
      </Snackbar>

    </Box>
  );
};

export default Integrations;
